import React, { useState, useEffect, useContext } from 'react';
import { Container, Table, Button, Modal, Form, Alert, Badge, Card } from 'react-bootstrap';
import { AuthContext } from '../contexts/AuthContext';
import { FaEdit, FaTrash, FaPlus, FaExternalLinkAlt } from 'react-icons/fa';

const ModBoards = () => {
  const { token, role } = useContext(AuthContext);
  const [modBoards, setModBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBoard, setEditingBoard] = useState(null);
  const [alert, setAlert] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    purchase_links: [{ vendor: '', url: '' }],
    active: true
  });

  // Fetch mod boards
  const fetchModBoards = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/mod-boards`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setModBoards(data);
      } else {
        throw new Error('Failed to fetch mod boards');
      }
    } catch (error) {
      console.error('Error fetching mod boards:', error);
      setAlert({ type: 'danger', message: 'Failed to load mod boards' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (role === 'super') {
      fetchModBoards();
    }
  }, [role, token]);

  // Handle form changes
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Handle purchase links changes
  const handlePurchaseLinkChange = (index, field, value) => {
    const updatedLinks = [...formData.purchase_links];
    updatedLinks[index] = { ...updatedLinks[index], [field]: value };
    setFormData(prev => ({ ...prev, purchase_links: updatedLinks }));
  };

  // Add new purchase link
  const addPurchaseLink = () => {
    setFormData(prev => ({
      ...prev,
      purchase_links: [...prev.purchase_links, { vendor: '', url: '' }]
    }));
  };

  // Remove purchase link
  const removePurchaseLink = (index) => {
    const updatedLinks = formData.purchase_links.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, purchase_links: updatedLinks }));
  };

  // Handle compatibility changes
  const handleCompatibilityChange = (value) => {
    const compatibilityArray = value.split(',').map(item => item.trim()).filter(Boolean);
    setFormData(prev => ({ ...prev, compatibility: compatibilityArray }));
  };

  // Open modal for add/edit
  const openModal = (board = null) => {
    if (board) {
      setEditingBoard(board);
      setFormData({
        name: board.name || '',
        purchase_links: board.purchase_links?.length > 0 
          ? board.purchase_links 
          : [{ vendor: '', url: '' }],
        active: board.active !== false
      });
    } else {
      setEditingBoard(null);
      setFormData({
        name: '',
        purchase_links: [{ vendor: '', url: ''}],
        active: true
      });
    }
    setShowModal(true);
  };

  // Close modal
  const closeModal = () => {
    setShowModal(false);
    setEditingBoard(null);
    setAlert(null);
  };

  // Save mod board
  const saveModBoard = async () => {
    try {
      // Validate required fields
      if (!formData.name.trim()) {
        setAlert({ type: 'danger', message: 'Name is required' });
        return;
      }

      // Filter out empty purchase links
      const validPurchaseLinks = formData.purchase_links.filter(
        link => link.vendor.trim() && link.url.trim()
      );

      const submitData = {
        ...formData,
        name: formData.name.trim(),
        purchase_links: validPurchaseLinks
      };

      const url = editingBoard 
        ? `${process.env.REACT_APP_API_URL}/mod-boards/${editingBoard._id}`
        : `${process.env.REACT_APP_API_URL}/mod-boards`;
      
      const method = editingBoard ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(submitData)
      });

      if (response.ok) {
        setAlert({ 
          type: 'success', 
          message: `Mod board ${editingBoard ? 'updated' : 'created'} successfully` 
        });
        await fetchModBoards();
        setTimeout(closeModal, 1500);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save mod board');
      }
    } catch (error) {
      console.error('Error saving mod board:', error);
      setAlert({ type: 'danger', message: error.message });
    }
  };

  // Delete mod board
  const deleteModBoard = async (boardId) => {
    if (!window.confirm('Are you sure you want to delete this mod board?')) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/mod-boards/${boardId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        setAlert({ type: 'success', message: 'Mod board deleted successfully' });
        await fetchModBoards();
      } else {
        throw new Error('Failed to delete mod board');
      }
    } catch (error) {
      console.error('Error deleting mod board:', error);
      setAlert({ type: 'danger', message: 'Failed to delete mod board' });
    }
  };

  if (role !== 'super') {
    return (
      <Container>
        <Alert variant="danger">Access denied. Super admin privileges required.</Alert>
      </Container>
    );
  }

  return (
    <Container>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Mod Boards Management</h2>
        <Button variant="primary" onClick={() => openModal()}>
          <FaPlus className="me-2" />
          Add Mod Board
        </Button>
      </div>

      {alert && (
        <Alert variant={alert.type} onClose={() => setAlert(null)} dismissible>
          {alert.message}
        </Alert>
      )}

      {loading ? (
        <div>Loading...</div>
      ) : (
        <Table responsive striped bordered hover>
          <thead>
            <tr>
              <th>Name</th>
              <th>Purchase Links</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {modBoards.map(board => (
              <tr key={board._id}>
                <td>
                  {board.name}
                </td>
                <td>
                  {board.purchase_links?.map((link, index) => (
                    <div key={index} className="mb-1">
                      <a 
                        href={link.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-decoration-none"
                      >
                        <FaExternalLinkAlt className="me-1" />
                        {link.vendor}
                        {link.price && ` - $${link.price}`}
                      </a>
                    </div>
                  ))}
                </td>
                <td>
                  <Badge bg={board.active ? 'success' : 'secondary'}>
                    {board.active ? 'Active' : 'Inactive'}
                  </Badge>
                </td>
                <td>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="me-2"
                    onClick={() => openModal(board)}
                  >
                    <FaEdit />
                  </Button>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => deleteModBoard(board._id)}
                  >
                    <FaTrash />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={closeModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingBoard ? 'Edit Mod Board' : 'Add New Mod Board'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {alert && (
            <Alert variant={alert.type} className="mb-3">
              {alert.message}
            </Alert>
          )}

          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Name *</Form.Label>
              <Form.Control
                type="text"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="e.g., Sunthar RGB Mux v1.3B"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Purchase Links</Form.Label>
              {formData.purchase_links.map((link, index) => (
                <Card key={index} className="mb-2 p-3">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <h6 className="mb-0">Link {index + 1}</h6>
                    {formData.purchase_links.length > 1 && (
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => removePurchaseLink(index)}
                      >
                        <FaTrash />
                      </Button>
                    )}
                  </div>
                  <div className="row">
                    <div className="col-md-6">
                      <Form.Label>Vendor</Form.Label>
                      <Form.Control
                        type="text"
                        value={link.vendor}
                        onChange={(e) => handlePurchaseLinkChange(index, 'vendor', e.target.value)}
                        placeholder="e.g., Sunthar Store"
                      />
                    </div>
                    <div className="col-md-6">
                      <Form.Label>URL</Form.Label>
                      <Form.Control
                        type="url"
                        value={link.url}
                        onChange={(e) => handlePurchaseLinkChange(index, 'url', e.target.value)}
                        placeholder="https://..."
                      />
                    </div>
                  </div>
                </Card>
              ))}
              <Button
                variant="outline-primary"
                size="sm"
                onClick={addPurchaseLink}
              >
                <FaPlus className="me-1" />
                Add Purchase Link
              </Button>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Active"
                checked={formData.active}
                onChange={(e) => handleChange('active', e.target.checked)}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={saveModBoard}>
            {editingBoard ? 'Update' : 'Create'} Mod Board
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default ModBoards; 